import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { FullPageSpinner } from '@/components/common/LoadingSpinner';
import ProtectedRoute from './ProtectedRoute';
import AppShell from '@/components/layout/AppShell';

// Auth pages
import LoginPage           from '@/pages/auth/LoginPage';
import RegisterPage        from '@/pages/auth/RegisterPage';
import ForgotPasswordPage  from '@/pages/auth/ForgotPasswordPage';
import PendingApprovalPage from '@/pages/auth/PendingApprovalPage';
import RejectedPage        from '@/pages/auth/RejectedPage';
import DeactivatedPage     from '@/pages/auth/DeactivatedPage';

// App pages
import DashboardPage  from '@/pages/DashboardPage';
import UsersPage      from '@/pages/users/UsersPage';
import ContentPage    from '@/pages/content/ContentPage';
import DayEditorPage  from '@/pages/content/DayEditorPage';
import AuditLogPage   from '@/pages/audit/AuditLogPage';

// TM (Team Manager) pages — /tm/*
import { TMAuthProvider, useTMAuth } from '@/contexts/TMAuthContext';
import TMShell        from '@/components/tm/TMShell';
import TMLoginPage    from '@/pages/tm/TMLoginPage';
import TMRegisterPage from '@/pages/tm/TMRegisterPage';
import TMPendingPage  from '@/pages/tm/TMPendingPage';
import TMDashboardPage from '@/pages/tm/TMDashboardPage';
import TaskListPage   from '@/pages/tm/TaskListPage';
import TaskDetailPage from '@/pages/tm/TaskDetailPage';
import CreateTaskPage from '@/pages/tm/CreateTaskPage';
import TMUsersPage    from '@/pages/tm/TMUsersPage';
import TMProfilePage  from '@/pages/tm/TMProfilePage';

// Public route — redirects to dashboard if already approved
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isApproved, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (isAuthenticated && isApproved) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Wrapper that applies AppShell to protected pages
function ShellRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  return (
    <ProtectedRoute requireAdmin={requireAdmin}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export default function AppRouter() {
  return (
    <Routes>

      {/* ── Public auth routes ──────────────────────────────────────────── */}
      <Route path="/login"          element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register"       element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

      {/* ── Account status routes ───────────────────────────────────────── */}
      <Route path="/pending"     element={<PendingApprovalPage />} />
      <Route path="/rejected"    element={<RejectedPage />} />
      <Route path="/deactivated" element={<DeactivatedPage />} />

      {/* ── Protected app routes (with AppShell) ───────────────────────── */}
      <Route path="/dashboard" element={<ShellRoute><DashboardPage /></ShellRoute>} />
      <Route path="/users"     element={<ShellRoute requireAdmin><UsersPage /></ShellRoute>} />

      {/* Content routes */}
      <Route path="/content"                             element={<Navigate to="/content/mothers" replace />} />
      <Route path="/content/:audience"                   element={<ShellRoute><ContentPage /></ShellRoute>} />
      <Route path="/content/:audience/day/:dayNumber"    element={<ShellRoute><DayEditorPage /></ShellRoute>} />

      {/* Phase 11+ */}
      <Route path="/audit"    element={<ShellRoute><AuditLogPage /></ShellRoute>} />
      {/* <Route path="/settings" element={<ShellRoute requireAdmin><SettingsPage /></ShellRoute>} /> */}

      {/* ── /tm — Team Manager (separate auth, mobile-first) ───────────── */}
      <Route path="/tm/*" element={<TMModule />} />

      {/* ── Default ─────────────────────────────────────────────────────── */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />

    </Routes>
  );
}

// ── TM sub-router ──────────────────────────────────────────────────────────────

function TMProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { firebaseUser, tmUser, loading, isTMApproved, isTMAdmin } = useTMAuth();
  if (loading) return <FullPageSpinner />;
  if (!firebaseUser) return <Navigate to="/tm" replace />;
  // No tmUsers doc means they haven't registered for TM yet — send to register
  if (!tmUser) return <Navigate to="/tm/register" replace />;
  if (tmUser.status === 'pending') return <Navigate to="/tm/pending" replace />;
  if (tmUser.status === 'rejected' || tmUser.status === 'deactivated') return <Navigate to="/tm" replace />;
  if (!isTMApproved) return <Navigate to="/tm/pending" replace />;
  if (adminOnly && !isTMAdmin) return <Navigate to="/tm/dashboard" replace />;
  return <TMShell>{children}</TMShell>;
}

function TMModule() {
  return (
    <TMAuthProvider>
      <Routes>
        <Route index element={<TMLoginPage />} />
        <Route path="register" element={<TMRegisterPage />} />
        <Route path="pending"  element={<TMPendingPage />} />
        <Route path="dashboard" element={<TMProtectedRoute><TMDashboardPage /></TMProtectedRoute>} />
        <Route path="tasks"     element={<TMProtectedRoute><TaskListPage /></TMProtectedRoute>} />
        <Route path="tasks/new" element={<TMProtectedRoute><CreateTaskPage /></TMProtectedRoute>} />
        <Route path="tasks/:id" element={<TMProtectedRoute><TaskDetailPage /></TMProtectedRoute>} />
        <Route path="users"     element={<TMProtectedRoute adminOnly><TMUsersPage /></TMProtectedRoute>} />
        <Route path="profile"   element={<TMProtectedRoute><TMProfilePage /></TMProtectedRoute>} />
        <Route path="*"         element={<Navigate to="/tm/dashboard" replace />} />
      </Routes>
    </TMAuthProvider>
  );
}
